import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Camera, Play, Pause } from 'lucide-react';
import { format } from 'date-fns';
import { WebcamSnapshot } from '@/utils/webcam-storage';

interface WebcamGalleryProps {
  testResponseId: string;
}

export function WebcamGallery({ testResponseId }: WebcamGalleryProps) {
  const [snapshots, setSnapshots] = useState<WebcamSnapshot[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(500); // ms between frames
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchSnapshots = async () => {
      try {
        const { data, error } = await supabase
          .from('test_webcam_snapshots')
          .select('*')
          .eq('test_response_id', testResponseId)
          .order('captured_at', { ascending: true });

        if (error) {
          console.error('Error fetching webcam snapshots:', error);
          return;
        }

        const snapshotsData = data || [];
        setSnapshots(snapshotsData);

        // Generate proxy URLs for all snapshots
        const { generateMultipleWebcamProxyUrls } = await import('@/utils/webcam-storage');
        const urlMap = await generateMultipleWebcamProxyUrls(snapshotsData);
        
        // Fetch actual images with authentication
        const imagePromises = Object.entries(urlMap).map(async ([id, url]) => {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) {
              console.error('No valid session token for fetching image:', id);
              return { id, url: null };
            }

            const response = await fetch(url, {
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
              },
            });

            if (!response.ok) {
              console.error('Failed to fetch webcam image:', response.status, id);
              return { id, url: null };
            }

            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            return { id, url: objectUrl };
          } catch (error) {
            console.error('Error fetching webcam image:', id, error);
            return { id, url: null };
          }
        });

        const imageResults = await Promise.all(imagePromises);
        const imageUrlMap = imageResults.reduce((acc, result) => {
          if (result.url) {
            acc[result.id] = result.url;
          }
          return acc;
        }, {} as Record<string, string>);

        setSignedUrls(imageUrlMap);
      } catch (error) {
        console.error('Error fetching webcam snapshots:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSnapshots();
  }, [testResponseId]);

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying || snapshots.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % snapshots.length);
    }, playbackSpeed);

    return () => clearInterval(interval);
  }, [isPlaying, snapshots.length, playbackSpeed]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + snapshots.length) % snapshots.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % snapshots.length);
  };


  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Webcam Monitoring
          </CardTitle>
        </CardHeader>
        <CardContent className="py-6">
          <div className="flex items-center justify-center">
            <p className="text-muted-foreground">Loading webcam snapshots...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (snapshots.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Webcam Monitoring
          </CardTitle>
        </CardHeader>
        <CardContent className="py-6">
          <div className="text-center">
            <Camera className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-2">No webcam snapshots available</p>
            <p className="text-sm text-muted-foreground">
              Either webcam monitoring was disabled for this test or no snapshots were captured.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentSnapshot = snapshots[currentIndex];
  const currentSignedUrl = currentSnapshot ? signedUrls[currentSnapshot.id] : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Webcam Monitoring
            <Badge variant="secondary">
              {snapshots.length} snapshots
            </Badge>
          </CardTitle>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsPlaying(!isPlaying)}
              className="flex items-center gap-1"
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {isPlaying ? 'Pause' : 'Play'}
            </Button>
            
            <select
              value={playbackSpeed}
              onChange={(e) => setPlaybackSpeed(Number(e.target.value))}
              className="text-sm border rounded px-2 py-1"
            >
              <option value={200}>5x Speed</option>
              <option value={500}>2x Speed</option>
              <option value={1000}>1x Speed</option>
              <option value={2000}>0.5x Speed</option>
            </select>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Image Display */}
        <div className="relative bg-muted rounded-lg overflow-hidden">
          <img
            src={currentSignedUrl || '/placeholder.svg'}
            alt={`Webcam snapshot ${currentIndex + 1}`}
            className="w-full h-64 object-contain"
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/placeholder.svg';
            }}
          />
          
          {/* Timestamp Overlay */}
          <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
            {format(new Date(currentSnapshot.captured_at), 'HH:mm:ss')}
          </div>
          
          {/* Index Overlay */}
          <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
            {currentIndex + 1} / {snapshots.length}
          </div>
        </div>
        
        {/* Navigation Controls */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={snapshots.length <= 1}
            className="flex items-center gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          
          <div className="text-sm text-muted-foreground text-center">
            <p>{format(new Date(currentSnapshot.captured_at), 'PPpp')}</p>
            {currentSnapshot.question_id && (
              <p className="text-xs">Question: {currentSnapshot.question_id.slice(0, 8)}...</p>
            )}
          </div>
          
          <Button
            variant="outline"
            onClick={handleNext}
            disabled={snapshots.length <= 1}
            className="flex items-center gap-1"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Timeline Scrubber */}
        {snapshots.length > 1 && (
          <div className="space-y-2">
            <input
              type="range"
              min={0}
              max={snapshots.length - 1}
              value={currentIndex}
              onChange={(e) => setCurrentIndex(Number(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{format(new Date(snapshots[0].captured_at), 'HH:mm:ss')}</span>
              <span>{format(new Date(snapshots[snapshots.length - 1].captured_at), 'HH:mm:ss')}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}